import { ChevronDown, ChevronUp, MapPin, Pencil, Trash2 } from "lucide-react";
import { NumberTile } from "./kit";
import { counterText, NOT_RECORDED } from "../lib/counters";
import { formatClockTime12h } from "../lib/clockTime";
import { srText, toRateText } from "../lib/recordFigures";
import { track } from "../lib/usageTracker";

/**
 * Issue #1030 (D107, D130, #726) — the phone match card of the redrawn
 * `/performance` list, `MatchListFold.dc.html`. Behind `newUi("matches")`;
 * `PerformanceMatchCard.jsx`'s `MatchCard` stays, untouched, for the
 * flag-off page.
 *
 * FOLDED IS THE DEFAULT. The folded region (`match-card-fold`) is the
 * opponent and date, then three stretched tiles (Runs with the not-out
 * star, Runs conceded, Wkts — Martin's "title and date plus runs, bowling
 * runs and wickets", D107, with the bowling figure named D153's way), then
 * the chevron. Open, the card takes a navy border and a `match-card-detail`
 * region under a rule: time · competition · season · team, and the figures
 * the desktop table carries (Pos, Balls, 4s, 6s, SR, Econ, Dismissal, #110:
 * nothing is dropped at phone width) plus Catches and Run outs (D154, which
 * the drawing omits; Andreas's ruling D206.5 Q4 builds them).
 *
 * KEEPER COUNTS (#704, review F1): "Caught behind" and "Stumpings" show only
 * when `kept_wicket === true`. `Catches` is the fielding catches counter
 * alone (#706's semantics are not in scope here).
 *
 * THE TAP TARGETS. The whole folded region opens the card (a pointer
 * convenience); the chevron is the real control, a `button` carrying
 * `aria-expanded`/`aria-controls`, so a keyboard or screen-reader user
 * never depends on the region's click. The pencil and the bin sit inside
 * that region and stop the click, so they never toggle (D107: "do not open
 * the card"). All three are 44px square.
 *
 * Every figure reads through `counterText` (R-005): a dash for a figure
 * never entered, a real 0 for a duck or a wicketless spell. Colours are kit
 * tokens, never `text-slate-*` (`index.css` remaps those with `!important`).
 */
const STOP = (e) => e.stopPropagation();

function Tap({ children, style, ...props }) {
  return (
    <button
      type="button"
      {...props}
      style={{ width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0, ...style }}
    >
      {children}
    </button>
  );
}

function detailLine(m) {
  const time = formatClockTime12h(m.match_time);
  return [time, m.competition || NOT_RECORDED, m.season || NOT_RECORDED, `Team: ${m.team || NOT_RECORDED}`].filter(Boolean).join(" · ");
}

export const GROUND_NOT_RECORDED = "Ground not recorded";

/**
 * #1293 (D214 L6, round-12 `MatchCardGround`) — the ground of a match for the
 * opened card: `{ name, town }`, or `null` when no ground name is stored (an
 * older match). `town` is `null` when `ground_city` is empty or absent.
 */
export function groundLine(m) {
  const name = (m.ground || "").trim();
  if (!name) return null;
  return { name, town: (m.ground_city || "").trim() || null };
}

export default function MatchCardFold({ m, open, onToggle: toggleFold, onEdit, onDelete }) {
  // Only OPENING is counted; folding it again is not.
  const onToggle = () => {
    if (!open) track("record.fold_open");
    toggleFold();
  };
  const id = m.match_id;
  const star = m.batting?.runs != null && m.batting?.not_out ? "*" : "";
  const Chevron = open ? ChevronUp : ChevronDown;
  const ground = groundLine(m);
  return (
    <div
      data-testid={`match-card-${id}`}
      data-state={open ? "open" : "folded"}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "12px 14px",
        background: "var(--nw-surface)",
        border: open ? "1px solid var(--nw-navy)" : "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      {/* `role="presentation"`: the region's click is a pointer convenience
          only; the chevron below is the keyboard and screen-reader control
          for the same toggle, so this element is not itself a widget. */}
      <div data-testid="match-card-fold" role="presentation" onClick={onToggle} style={{ display: "flex", flexDirection: "column", gap: 8, cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, paddingTop: 4 }}>
            <div className="nw-rowname" style={{ overflowWrap: "anywhere" }}>{m.opponent}</div>
            <div className="nw-help">{m.match_date || NOT_RECORDED}</div>
          </div>
          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
            <Tap aria-label={`Edit match v ${m.opponent}`} data-testid={`edit-card-${id}`} onClick={(e) => { STOP(e); onEdit(); }}>
              <Pencil size={16} style={{ color: "var(--nw-navy)" }} aria-hidden="true" />
            </Tap>
            <Tap aria-label={`Delete match v ${m.opponent}`} data-testid={`del-card-${id}`} onClick={(e) => { STOP(e); onDelete(); }}>
              <Trash2 size={16} style={{ color: "var(--nw-alert)" }} aria-hidden="true" />
            </Tap>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "stretch", gap: 6 }}>
          <NumberTile variant="tile" label="Runs" value={`${counterText(m.batting?.runs)}${star}`} accent="var(--nw-navy)" style={{ flex: 1 }} data-testid={`match-card-runs-${id}`} />
          <NumberTile variant="tile" label="Runs conceded" value={counterText(m.bowling?.runs_conceded)} style={{ flex: 1 }} data-testid={`match-card-conceded-${id}`} />
          <NumberTile variant="tile" label="Wkts" value={counterText(m.bowling?.wickets)} style={{ flex: 1 }} data-testid={`match-card-wkts-${id}`} />
          <Tap
            aria-label={open ? `Fold match v ${m.opponent}` : `Show all figures v ${m.opponent}`}
            aria-expanded={open}
            aria-controls={open ? `match-card-detail-${id}` : undefined}
            data-testid="match-card-toggle"
            onClick={(e) => { STOP(e); onToggle(); }}
            style={{ alignSelf: "center" }}
          >
            <Chevron size={16} strokeWidth={2.5} style={{ color: open ? "var(--nw-navy)" : "var(--nw-ink-muted)" }} aria-hidden="true" />
          </Tap>
        </div>
      </div>
      {open && (
        <div
          id={`match-card-detail-${id}`}
          data-testid="match-card-detail"
          style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 8, borderTop: "1px solid var(--nw-line-faint)" }}
        >
          <div data-testid={`match-card-ground-${id}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
            <MapPin size={16} strokeWidth={2} aria-hidden="true" color={ground ? "var(--nw-navy)" : "var(--nw-ink-faint)"} style={{ flexShrink: 0 }} />
            {ground ? (
              // One inline run, so a long name wraps under the pin with the town after it.
              <span style={{ minWidth: 0 }}>
                <span style={{ fontWeight: 700, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>{ground.name}</span>
                {ground.town && <span style={{ color: "var(--nw-ink-muted)" }}>{` · ${ground.town}`}</span>}
              </span>
            ) : (
              <span style={{ color: "var(--nw-ink-muted)" }}>{GROUND_NOT_RECORDED}</span>
            )}
          </div>
          <div className="nw-help">{detailLine(m)}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 6 }}>
            <NumberTile variant="tile" label="Pos" value={counterText(m.batting?.batting_position)} />
            <NumberTile variant="tile" label="Balls" value={counterText(m.batting?.balls_faced)} />
            <NumberTile variant="tile" label="4s" value={counterText(m.batting?.fours)} />
            <NumberTile variant="tile" label="6s" value={counterText(m.batting?.sixes)} />
            {/* D102 — strike rate to one decimal, economy to two. */}
            <NumberTile variant="tile" label="SR" value={srText(m.batting?.strike_rate)} />
            <NumberTile variant="tile" label="Econ" value={toRateText(m.bowling?.economy_rate)} />
            <NumberTile variant="tile" label="Catches" value={counterText(m.fielding?.fielding_catches)} />
            <NumberTile variant="tile" label="Run outs" value={counterText(m.fielding?.run_outs)} />
            {/* #704 (closed 2026-09-29): the keeper's own counters, only for a match
                where the player kept (`kept_wicket === true`; null is never asked,
                false is "no"). `Catches` above stays the fielding counter alone. */}
            {m.kept_wicket === true && (
              <>
                <NumberTile variant="tile" label="Caught behind" value={counterText(m.fielding?.wicketkeeping_catches)} style={{ gridColumn: "span 2" }} />
                <NumberTile variant="tile" label="Stumpings" value={counterText(m.fielding?.stumpings)} style={{ gridColumn: "span 2" }} />
              </>
            )}
            {/* `dismissal_type` and `not_out` are independent fields; a
                not-out innings need not carry the string (MatchRow.jsx B7). */}
            <NumberTile
              variant="tile"
              label="Dismissal"
              value={m.batting?.dismissal_type || (m.batting?.not_out ? "Not out" : NOT_RECORDED)}
              style={{ gridColumn: "span 4" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
